import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  onSnapshot,
  Timestamp,
  writeBatch,
  limit
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { 
  Engagement, 
  EngagementFormData,
  QuickCreateFormData,
  EngagementFilters,
  EngagementSortOptions,
  EngagementStatistics,
  EngagementType,
  EngagementStatus,
  EngagementTemplate,
  TeamAllocation,
  EngagementMilestone,
  RecurringTaskTemplate,
  SLADefinition,
  ContractRenewal,
  ENGAGEMENT_TYPE_CONFIG
} from '../types/engagement';
import { Opportunity, Offer } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { tasksService } from './tasksService';

// Helper to convert Firestore timestamps
const convertTimestamps = (data: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value instanceof Timestamp) {
      result[key] = value.toDate();
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = convertTimestamps(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      result[key] = value.map(item => 
        item && typeof item === 'object' ? convertTimestamps(item as Record<string, unknown>) : item
      );
    } else {
      result[key] = value;
    }
  }
  return result;
};

// Helper to convert dates to Firestore timestamps and remove undefined values
const convertDatesToTimestamps = (data: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    // Skip undefined values - Firestore doesn't accept them
    if (value === undefined) {
      continue;
    }
    if (value instanceof Date) {
      result[key] = Timestamp.fromDate(value);
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = convertDatesToTimestamps(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      result[key] = value.map(item => 
        item && typeof item === 'object' ? convertDatesToTimestamps(item as Record<string, unknown>) : item
      );
    } else {
      result[key] = value;
    }
  }
  return result;
};

export const engagementsService = {
  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  async createEngagement(orgId: string, data: EngagementFormData): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const typeConfig = ENGAGEMENT_TYPE_CONFIG[data.engagementType];
    
    // Generate IDs for milestones, recurring tasks, and SLAs
    const milestones = data.milestones?.map((m, index) => ({
      ...m,
      id: uuidv4(),
      status: 'pending' as const,
      order: index
    })) || [];

    const recurringTasks = data.recurringTasks?.map(t => ({
      ...t,
      id: uuidv4()
    })) || [];

    const slaDefinitions = data.slaDefinitions?.map(s => ({
      ...s,
      id: uuidv4()
    })) || [];

    const engagement: Omit<Engagement, 'id'> = {
      orgId,
      name: data.name,
      description: data.description,
      engagementType: data.engagementType,
      billingType: data.billingType,
      status: 'draft',
      complexity: data.complexity || typeConfig.complexity,
      startDate: data.startDate,
      endDate: data.endDate,
      isOpenEnded: !typeConfig.hasEndDate || data.isOpenEnded,
      contractEndDate: data.contractEndDate,
      milestones: typeConfig.hasMilestones ? milestones : undefined,
      isInternal: data.isInternal,
      customerId: data.customerId,
      customerNameManual: data.customerNameManual,
      opportunityId: data.opportunityId,
      offerId: data.offerId,
      contactIds: data.contactIds,
      productIds: data.productIds,
      managerId: data.managerId,
      managerName: '', // Will be filled by caller or trigger
      teamAllocations: data.teamAllocations,
      fteAllocations: data.fteAllocations,
      budget: data.budget as Engagement['budget'],
      taskIds: [],
      recurringTasks: typeConfig.hasRecurringTasks ? recurringTasks : undefined,
      slaDefinitions: typeConfig.hasSLA ? slaDefinitions : undefined,
      documents: [],
      objectives: data.objectives,
      deliverables: data.deliverables,
      createdFromTemplateId: data.templateId,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: user.uid
    };

    const docData = convertDatesToTimestamps(engagement as unknown as Record<string, unknown>);
    const docRef = await addDoc(collection(db, 'engagements'), docData);
    return docRef.id;
  },

  async quickCreateEngagement(orgId: string, data: QuickCreateFormData): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const typeConfig = ENGAGEMENT_TYPE_CONFIG[data.engagementType];
    
    const engagement: Omit<Engagement, 'id'> = {
      orgId,
      name: data.name,
      engagementType: data.engagementType,
      billingType: 'time_and_material', // Default
      status: 'draft',
      complexity: typeConfig.complexity,
      startDate: data.startDate,
      isOpenEnded: !typeConfig.hasEndDate,
      isInternal: !data.customerId && !data.customerNameManual,
      customerId: data.customerId,
      customerNameManual: data.customerNameManual,
      managerId: data.managerId,
      managerName: '', // Will be filled
      teamAllocations: [],
      taskIds: [],
      documents: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: user.uid
    };

    const docData = convertDatesToTimestamps(engagement as unknown as Record<string, unknown>);
    const docRef = await addDoc(collection(db, 'engagements'), docData);
    return docRef.id;
  },

  async updateEngagement(engagementId: string, data: Partial<Engagement>): Promise<void> {
    const updateData = {
      ...convertDatesToTimestamps(data as unknown as Record<string, unknown>),
      updatedAt: Timestamp.now()
    };
    await updateDoc(doc(db, 'engagements', engagementId), updateData);
  },

  async deleteEngagement(engagementId: string): Promise<void> {
    await deleteDoc(doc(db, 'engagements', engagementId));
  },

  async getEngagement(engagementId: string): Promise<Engagement | null> {
    const docSnap = await getDoc(doc(db, 'engagements', engagementId));
    if (!docSnap.exists()) return null;
    
    const data = convertTimestamps(docSnap.data() as Record<string, unknown>);
    return { id: docSnap.id, ...data } as Engagement;
  },

  async getEngagements(orgId: string, filters?: EngagementFilters, sort?: EngagementSortOptions): Promise<Engagement[]> {
    let q = query(
      collection(db, 'engagements'),
      where('orgId', '==', orgId)
    );

    // Apply filters
    if (filters?.engagementTypes && filters.engagementTypes.length > 0) {
      q = query(q, where('engagementType', 'in', filters.engagementTypes));
    }
    if (filters?.statuses && filters.statuses.length > 0) {
      q = query(q, where('status', 'in', filters.statuses));
    }
    if (filters?.customerId) {
      q = query(q, where('customerId', '==', filters.customerId));
    }
    if (filters?.managerId) {
      q = query(q, where('managerId', '==', filters.managerId));
    }
    if (filters?.isInternal !== undefined) {
      q = query(q, where('isInternal', '==', filters.isInternal));
    }

    // Apply sorting
    const sortField = sort?.field || 'updatedAt';
    const sortDirection = sort?.direction || 'desc';
    q = query(q, orderBy(sortField, sortDirection));

    const snapshot = await getDocs(q);
    const engagements = snapshot.docs.map(doc => {
      const data = convertTimestamps(doc.data() as Record<string, unknown>);
      return { id: doc.id, ...data } as Engagement;
    });

    // Apply client-side filters for team member
    if (filters?.teamMemberId) {
      return engagements.filter(e => 
        e.teamAllocations.some(ta => ta.userId === filters.teamMemberId) ||
        e.fteAllocations?.some(fa => fa.userId === filters.teamMemberId)
      );
    }

    // Apply search filter
    if (filters?.searchQuery) {
      const search = filters.searchQuery.toLowerCase();
      return engagements.filter(e => 
        e.name.toLowerCase().includes(search) ||
        e.description?.toLowerCase().includes(search) ||
        e.customerNameManual?.toLowerCase().includes(search)
      );
    }

    return engagements;
  },

  // Engagements linked to a customer (company) — targeted single-field query
  // (no composite index needed), org-scoped client-side for defense in depth.
  async getEngagementsByCustomer(orgId: string, customerId: string): Promise<Engagement[]> {
    try {
      const snap = await getDocs(query(collection(db, 'engagements'), where('customerId', '==', customerId)));
      return snap.docs
        .map(doc => {
          const data = convertTimestamps(doc.data() as Record<string, unknown>);
          return { id: doc.id, ...data } as Engagement;
        })
        .filter(e => e.orgId === orgId);
    } catch (error) {
      console.warn('Error getting engagements by customer:', error);
      return [];
    }
  },

  subscribeToEngagements(
    orgId: string,
    callback: (engagements: Engagement[]) => void,
    filters?: EngagementFilters
  ): () => void {
    let q = query(
      collection(db, 'engagements'),
      where('orgId', '==', orgId),
      orderBy('updatedAt', 'desc')
    );

    if (filters?.statuses && filters.statuses.length > 0) {
      q = query(
        collection(db, 'engagements'),
        where('orgId', '==', orgId),
        where('status', 'in', filters.statuses),
        orderBy('updatedAt', 'desc')
      );
    }

    return onSnapshot(q, (snapshot) => {
      const engagements = snapshot.docs.map(doc => {
        const data = convertTimestamps(doc.data() as Record<string, unknown>);
        return { id: doc.id, ...data } as Engagement;
      });
      callback(engagements);
    });
  },

  // ============================================================================
  // STATISTICS
  // ============================================================================

  async getStatistics(orgId: string): Promise<EngagementStatistics> {
    const engagements = await this.getEngagements(orgId);
    
    const byType: Partial<Record<EngagementType, number>> = {};
    const byStatus: Partial<Record<EngagementStatus, number>> = {};
    let totalBudget = 0;
    let usedBudget = 0;
    let overdueMilestones = 0;
    let expiringContracts = 0;
    const now = new Date();
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

    engagements.forEach(e => {
      // Count by type
      byType[e.engagementType] = (byType[e.engagementType] || 0) + 1;
      
      // Count by status
      byStatus[e.status] = (byStatus[e.status] || 0) + 1;
      
      // Budget
      if (e.budget?.totalBudget) {
        totalBudget += e.budget.totalBudget;
        if (e.budget.actualExpenses) {
          usedBudget += e.budget.actualExpenses;
        }
      }
      
      // Overdue milestones
      e.milestones?.forEach(m => {
        if (m.status !== 'completed' && new Date(m.dueDate) < now) {
          overdueMilestones++;
        }
      });
      
      // Expiring contracts
      if (e.contractEndDate && new Date(e.contractEndDate) <= threeMonthsFromNow) {
        expiringContracts++;
      }
    });

    const activeEngagements = engagements.filter(e => e.status === 'active').length;
    
    // Calculate upcoming deadlines (milestones in next 14 days)
    const twoWeeksFromNow = new Date();
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
    let upcomingDeadlines = 0;
    engagements.forEach(e => {
      e.milestones?.forEach(m => {
        const dueDate = new Date(m.dueDate);
        if (m.status !== 'completed' && dueDate >= now && dueDate <= twoWeeksFromNow) {
          upcomingDeadlines++;
        }
      });
    });

    return {
      totalEngagements: engagements.length,
      activeEngagements,
      byType,
      byStatus,
      totalBudget,
      usedBudget,
      teamUtilization: {
        averageUtilization: 0, // Calculated by utilizationService
        overallocatedMembers: 0,
        underutilizedMembers: 0
      },
      upcomingDeadlines,
      overdueMilestones,
      expiringContracts
    };
  },

  // ============================================================================
  // DEAL → DELIVERY: create an engagement from a (won) opportunity
  // ============================================================================

  /**
   * Create a project engagement pre-filled from an opportunity and linked back
   * to it (engagement.opportunityId). The caller should store the returned id on
   * the opportunity (opportunity.engagementId) to complete the bidirectional link.
   */
  async createFromOpportunity(orgId: string, opportunity: Opportunity): Promise<string> {
    const user = auth.currentUser;
    const data: EngagementFormData = {
      name: opportunity.title || 'Neuer Auftrag',
      description: '',
      engagementType: 'project',
      billingType: 'fixed_price',
      startDate: new Date(),
      isOpenEnded: false,
      isInternal: false,
      customerId: opportunity.companyId || undefined,
      opportunityId: opportunity.id,
      offerId: opportunity.offerId,
      productIds: opportunity.productId ? [opportunity.productId] : undefined,
      managerId: opportunity.assignedTo || user?.uid || '',
      teamAllocations: [],
      budget: opportunity.value ? { totalBudget: opportunity.value } : undefined,
    };
    return this.createEngagement(orgId, data);
  },

  /**
   * Create a project engagement pre-filled from an accepted offer/contract and
   * linked back to it (engagement.offerId / opportunityId). The caller should
   * store the returned id on the offer (offer.engagementId).
   */
  async createFromOffer(orgId: string, offer: Offer): Promise<string> {
    const user = auth.currentUser;
    const data: EngagementFormData = {
      name: offer.title || offer.offerNumber || 'Neuer Auftrag',
      description: '',
      engagementType: 'project',
      billingType: 'fixed_price',
      startDate: new Date(),
      isOpenEnded: false,
      isInternal: false,
      customerId: offer.companyId || undefined,
      opportunityId: offer.opportunityId,
      offerId: offer.id,
      contactIds: offer.contactId ? [offer.contactId] : undefined,
      managerId: offer.createdBy || user?.uid || '',
      teamAllocations: [],
      budget: offer.total ? { totalBudget: offer.total } : undefined,
    };
    return this.createEngagement(orgId, data);
  },

  // ============================================================================
  // CONVERSION (POC/Pilot → Project)
  // ============================================================================

  async convertToProject(engagementId: string): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const typeConfig = ENGAGEMENT_TYPE_CONFIG[engagement.engagementType];
    if (!typeConfig.canConvertToProject) {
      throw new Error('This engagement type cannot be converted to a project');
    }

    // Create new project from engagement
    const projectData: Omit<Engagement, 'id'> = {
      ...engagement,
      engagementType: 'project',
      status: 'planning',
      complexity: 'complex',
      isOpenEnded: false,
      convertedFromId: engagementId,
      convertedFromType: engagement.engagementType,
      milestones: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: user.uid
    };

    const docData = convertDatesToTimestamps(projectData as unknown as Record<string, unknown>);
    const docRef = await addDoc(collection(db, 'engagements'), docData);

    // Update original engagement
    await this.updateEngagement(engagementId, {
      status: 'completed',
      convertedToId: docRef.id
    });

    return docRef.id;
  },

  // ============================================================================
  // MILESTONES
  // ============================================================================

  async updateMilestone(engagementId: string, milestoneId: string, updates: Partial<EngagementMilestone>): Promise<void> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const milestones = engagement.milestones?.map(m => 
      m.id === milestoneId ? { ...m, ...updates } : m
    ) || [];

    // Calculate progress based on completed milestones
    const completedCount = milestones.filter(m => m.status === 'completed').length;
    const progressPercentage = milestones.length > 0 
      ? Math.round((completedCount / milestones.length) * 100) 
      : 0;

    await this.updateEngagement(engagementId, { 
      milestones,
      progressPercentage
    });
  },

  async addMilestone(engagementId: string, milestone: Omit<EngagementMilestone, 'id' | 'status'>): Promise<void> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const newMilestone: EngagementMilestone = {
      ...milestone,
      id: uuidv4(),
      status: 'pending'
    };

    const milestones = [...(engagement.milestones || []), newMilestone];
    await this.updateEngagement(engagementId, { milestones });
  },

  // ============================================================================
  // CONTRACT RENEWAL
  // ============================================================================

  async updateContractRenewal(engagementId: string, renewal: ContractRenewal): Promise<void> {
    await this.updateEngagement(engagementId, { contractRenewal: renewal });
  },

  async renewContract(engagementId: string, newEndDate: Date, notes?: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const previousEndDate = engagement.contractEndDate || engagement.endDate || new Date();
    
    const renewalHistory = engagement.contractRenewal?.renewalHistory || [];
    renewalHistory.push({
      id: uuidv4(),
      renewedAt: new Date(),
      previousEndDate,
      newEndDate,
      notes,
      renewedBy: user.uid
    });

    await this.updateEngagement(engagementId, {
      contractEndDate: newEndDate,
      contractRenewal: {
        ...engagement.contractRenewal,
        renewalStatus: 'renewed',
        renewedUntil: newEndDate,
        renewalHistory
      }
    });
  },

  async getExpiringContracts(orgId: string, withinDays: number = 90): Promise<Engagement[]> {
    const engagements = await this.getEngagements(orgId, {
      statuses: ['active']
    });

    const thresholdDate = new Date();
    thresholdDate.setDate(thresholdDate.getDate() + withinDays);

    return engagements.filter(e => {
      const endDate = e.contractEndDate || e.endDate;
      return endDate && new Date(endDate) <= thresholdDate;
    });
  },

  // ============================================================================
  // TEMPLATES
  // ============================================================================

  async getTemplates(orgId: string, engagementType?: EngagementType): Promise<EngagementTemplate[]> {
    let q = query(
      collection(db, 'engagementTemplates'),
      where('orgId', '==', orgId),
      orderBy('usageCount', 'desc')
    );

    if (engagementType) {
      q = query(
        collection(db, 'engagementTemplates'),
        where('orgId', '==', orgId),
        where('engagementType', '==', engagementType),
        orderBy('usageCount', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = convertTimestamps(doc.data() as Record<string, unknown>);
      return { id: doc.id, ...data } as EngagementTemplate;
    });
  },

  async createTemplate(orgId: string, template: Omit<EngagementTemplate, 'id' | 'orgId' | 'usageCount' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const templateData = {
      ...template,
      orgId,
      usageCount: 0,
      createdBy: user.uid,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'engagementTemplates'), templateData);
    return docRef.id;
  },

  async createTemplateFromEngagement(engagementId: string, templateName: string): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const template: Omit<EngagementTemplate, 'id' | 'orgId' | 'usageCount' | 'createdAt' | 'updatedAt'> = {
      name: templateName,
      description: `Erstellt aus: ${engagement.name}`,
      engagementType: engagement.engagementType,
      isDefault: false,
      defaultBillingType: engagement.billingType,
      defaultMilestones: engagement.milestones?.map(m => ({
        name: m.name,
        description: m.description,
        dueDate: m.dueDate,
        deliverables: m.deliverables,
        order: m.order
      })),
      defaultRecurringTasks: engagement.recurringTasks?.map(t => ({
        title: t.title,
        description: t.description,
        frequency: t.frequency,
        dayOfWeek: t.dayOfWeek,
        dayOfMonth: t.dayOfMonth,
        estimatedHours: t.estimatedHours,
        priority: t.priority
      })),
      defaultSLAs: engagement.slaDefinitions?.map(s => ({
        name: s.name,
        description: s.description,
        metric: s.metric,
        target: s.target,
        unit: s.unit,
        measurementPeriod: s.measurementPeriod,
        penaltyPerViolation: s.penaltyPerViolation,
        warningThreshold: s.warningThreshold
      })),
      createdBy: user.uid
    };

    return this.createTemplate(engagement.orgId, template);
  },

  async incrementTemplateUsage(templateId: string): Promise<void> {
    const templateDoc = doc(db, 'engagementTemplates', templateId);
    const templateSnap = await getDoc(templateDoc);
    if (templateSnap.exists()) {
      const currentCount = templateSnap.data().usageCount || 0;
      await updateDoc(templateDoc, { 
        usageCount: currentCount + 1,
        updatedAt: Timestamp.now()
      });
    }
  },

  // ============================================================================
  // TASKS INTEGRATION
  // ============================================================================

  async addTaskToEngagement(engagementId: string, taskId: string): Promise<void> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const taskIds = [...engagement.taskIds, taskId];
    await this.updateEngagement(engagementId, { taskIds });
  },

  async removeTaskFromEngagement(engagementId: string, taskId: string): Promise<void> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const taskIds = engagement.taskIds.filter(id => id !== taskId);
    await this.updateEngagement(engagementId, { taskIds });
  },

  // ============================================================================
  // RECURRING TASKS
  // ============================================================================

  async addRecurringTask(engagementId: string, task: Omit<RecurringTaskTemplate, 'id'>): Promise<void> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const newTask: RecurringTaskTemplate = {
      ...task,
      id: uuidv4()
    };

    const recurringTasks = [...(engagement.recurringTasks || []), newTask];
    await this.updateEngagement(engagementId, { recurringTasks });
  },

  async updateRecurringTask(engagementId: string, taskId: string, updates: Partial<RecurringTaskTemplate>): Promise<void> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const recurringTasks = engagement.recurringTasks?.map(t => 
      t.id === taskId ? { ...t, ...updates } : t
    ) || [];

    await this.updateEngagement(engagementId, { recurringTasks });
  },

  async deleteRecurringTask(engagementId: string, taskId: string): Promise<void> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const recurringTasks = engagement.recurringTasks?.filter(t => t.id !== taskId) || [];
    await this.updateEngagement(engagementId, { recurringTasks });
  },

  // ============================================================================
  // SLA DEFINITIONS
  // ============================================================================

  async addSLADefinition(engagementId: string, sla: Omit<SLADefinition, 'id'>): Promise<void> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const newSLA: SLADefinition = {
      ...sla,
      id: uuidv4()
    };

    const slaDefinitions = [...(engagement.slaDefinitions || []), newSLA];
    await this.updateEngagement(engagementId, { slaDefinitions });
  },

  async updateSLADefinition(engagementId: string, slaId: string, updates: Partial<SLADefinition>): Promise<void> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');

    const slaDefinitions = engagement.slaDefinitions?.map(s => 
      s.id === slaId ? { ...s, ...updates } : s
    ) || [];

    await this.updateEngagement(engagementId, { slaDefinitions });
  },

  // ============================================================================
  // RECURRING TASK GENERATION
  // ============================================================================

  async generateRecurringTasks(engagementId: string): Promise<string[]> {
    const engagement = await this.getEngagement(engagementId);
    if (!engagement) throw new Error('Engagement not found');
    if (!engagement.recurringTasks || engagement.recurringTasks.length === 0) return [];

    const createdTaskIds: string[] = [];
    const now = new Date();

    for (const template of engagement.recurringTasks) {
      // Check if task should be generated
      const lastGenerated = template.lastGeneratedAt ? new Date(template.lastGeneratedAt) : null;
      let shouldGenerate = false;
      let nextDueDate = new Date();

      switch (template.frequency) {
        case 'daily':
          shouldGenerate = !lastGenerated || now.getDate() !== lastGenerated.getDate();
          nextDueDate = new Date(now);
          nextDueDate.setDate(nextDueDate.getDate() + 1);
          break;
        case 'weekly':
          const dayOfWeek = template.dayOfWeek ?? 1; // Default Monday
          shouldGenerate = !lastGenerated || 
            now.getTime() - lastGenerated.getTime() >= 7 * 24 * 60 * 60 * 1000;
          nextDueDate = new Date(now);
          nextDueDate.setDate(nextDueDate.getDate() + ((7 + dayOfWeek - nextDueDate.getDay()) % 7 || 7));
          break;
        case 'biweekly':
          shouldGenerate = !lastGenerated || 
            now.getTime() - lastGenerated.getTime() >= 14 * 24 * 60 * 60 * 1000;
          nextDueDate = new Date(now);
          nextDueDate.setDate(nextDueDate.getDate() + 14);
          break;
        case 'monthly':
          const dayOfMonth = template.dayOfMonth ?? 1;
          shouldGenerate = !lastGenerated || 
            now.getMonth() !== lastGenerated.getMonth() || 
            now.getFullYear() !== lastGenerated.getFullYear();
          nextDueDate = new Date(now.getFullYear(), now.getMonth() + 1, dayOfMonth);
          break;
        case 'quarterly':
          shouldGenerate = !lastGenerated || 
            Math.floor(now.getMonth() / 3) !== Math.floor(lastGenerated.getMonth() / 3) ||
            now.getFullYear() !== lastGenerated.getFullYear();
          const currentQuarter = Math.floor(now.getMonth() / 3);
          nextDueDate = new Date(now.getFullYear(), (currentQuarter + 1) * 3, 1);
          break;
      }

      if (shouldGenerate) {
        const taskId = await tasksService.createRecurringTask(
          engagementId,
          template.id,
          {
            title: template.title,
            description: template.description,
            assignedTo: template.assignedToId || engagement.managerId,
            dueDate: nextDueDate,
            priority: template.priority,
            orgId: engagement.orgId,
            createdBy: engagement.managerId
          }
        );
        createdTaskIds.push(taskId);

        // Update template with last generated date
        await this.updateRecurringTask(engagementId, template.id, {
          lastGeneratedAt: now,
          nextDueDate
        });
      }
    }

    // Add created task IDs to engagement
    if (createdTaskIds.length > 0) {
      const newTaskIds = [...engagement.taskIds, ...createdTaskIds];
      await this.updateEngagement(engagementId, { taskIds: newTaskIds });
    }

    return createdTaskIds;
  },

  async generateAllRecurringTasks(orgId: string): Promise<{ engagementId: string; tasksCreated: number }[]> {
    const engagements = await this.getEngagements(orgId, {
      statuses: ['active']
    });

    const results: { engagementId: string; tasksCreated: number }[] = [];

    for (const engagement of engagements) {
      if (engagement.recurringTasks && engagement.recurringTasks.length > 0) {
        const taskIds = await this.generateRecurringTasks(engagement.id);
        results.push({
          engagementId: engagement.id,
          tasksCreated: taskIds.length
        });
      }
    }

    return results;
  }
};
